import React from 'react';
import { Badge, Button, Icon } from '../../ds';

const isEmpty = (v) => v == null || (typeof v === 'string' && v.trim() === '');

const detectField = (related, sourceLead) => {
    if (!related) return null;
    if (related._matchField) return related._matchField;
    if (related.field) {
        const f = String(related.field).toLowerCase();
        if (f === 'instagram' || f === 'ig') return 'IG';
        if (f === 'tiktok' || f === 'tt') return 'TT';
        if (f === 'email' || f === 'em') return 'EM';
    }
    if (sourceLead?.instagram && related.instagram &&
        sourceLead.instagram.toLowerCase() === related.instagram.toLowerCase()) return 'IG';
    if (sourceLead?.tiktok && related.tiktok &&
        sourceLead.tiktok.toLowerCase() === related.tiktok.toLowerCase()) return 'TT';
    if (sourceLead?.email && related.email &&
        sourceLead.email.toLowerCase() === related.email.toLowerCase()) return 'EM';
    return null;
};

const resolveHandle = (related) => {
    if (!isEmpty(related?.instagram)) return related.instagram;
    if (!isEmpty(related?.tiktok)) return related.tiktok;
    if (!isEmpty(related?.email)) return related.email;
    if (!isEmpty(related?.originalHandle)) return related.originalHandle;
    return related?.name || related?.originalName || 'Без имени';
};

const resolveManager = (related) =>
    related?.owner?.name ||
    related?.owner?.email ||
    related?.ownerName ||
    related?.scout?.name ||
    related?.originalScout ||
    related?.metadata?.scoutName ||
    null;

const resolveStatusLabel = (related) =>
    related?.statusLabel ||
    related?.status?.label ||
    null;

const daysInStatus = (related) => {
    if (related?.daysSinceStatusChange != null && Number.isFinite(related.daysSinceStatusChange)) {
        return related.daysSinceStatusChange;
    }
    const dateStr =
        related?.statusChangedAt ||
        related?.statusUpdatedAt ||
        related?.metadata?.statusUpdateDate ||
        related?.updatedAt;
    if (!dateStr) return null;
    const d = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
    return Number.isFinite(d) ? d : null;
};

const buildLineParts = (related, sourceLead) => {
    const parts = [];
    const field = detectField(related, sourceLead);
    if (field) parts.push(field);

    const handle = resolveHandle(related);
    if (handle) parts.push(handle);

    const manager = resolveManager(related);
    if (manager) parts.push(manager);

    const statusLabel = resolveStatusLabel(related);
    if (statusLabel) parts.push(statusLabel);

    const days = daysInStatus(related);
    if (days != null) parts.push(`${days} дн.`);

    return parts;
};

const TooltipLine = ({ related, sourceLead }) => {
    const parts = buildLineParts(related, sourceLead);
    return <div className='tooltip-content__line'>{parts.join(' · ')}</div>;
};

const TooltipVariant = ({ related, sourceLead, isDuplicate }) => {
    const badgeClass = isDuplicate ? 'duplicate-badge duplicate-badge--dup' : 'duplicate-badge duplicate-badge--orig';
    const badgeTitle = isDuplicate ? 'Дубль' : 'Оригинал';
    const badgeIcon = isDuplicate ? '⚠' : '✓';
    return (
        <div className='tooltip-wrapper'>
            <span className={badgeClass} title={badgeTitle}>{badgeIcon}</span>
            {related && related.length > 0 && (
                <div className='tooltip-content'>
                    {related.map((r, idx) => {
                        const isOrig = r._role === 'original';
                        const roleLabel = isOrig ? 'Оригинал' : 'Дубль';
                        return (
                            <div key={r.id ?? idx} className='tooltip-content__row tooltip-content__row--inline'>
                                <span className={`tooltip-content__role tooltip-content__role--${isOrig ? 'original' : 'duplicate'}`}>{roleLabel}</span>
                                <TooltipLine related={r} sourceLead={sourceLead} />
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const SectionLine = ({ related, sourceLead }) => {
    const parts = buildLineParts(related, sourceLead);
    return <span>{parts.join(' · ')}</span>;
};

const SectionVariant = ({ related, sourceLead, onNavigate, onMakeOriginal }) => (
    <div className='oc-lead-card__dup-list'>
        {(related || []).map((r) => {
            const isOrig = r._role === 'original';
            const handleClick = (e) => {
                e.preventDefault();
                if (onNavigate && r.id) onNavigate(r.id);
            };
            return (
                <div key={r.id} className='oc-lead-card__dup-row'>
                    <a
                        href={`?lead=${r.id}`}
                        onClick={handleClick}
                        className='oc-lead-card__dup-link'
                    >
                        <Badge color={isOrig ? 'green' : 'orange'} type='contained' size='small'>
                            {isOrig ? 'Оригинал' : 'Дубль'}
                        </Badge>
                        <span className={isOrig ? 'oc-lead-card__dup-text--original' : 'oc-lead-card__dup-text--duplicate'}>
                            <SectionLine related={r} sourceLead={sourceLead} />
                        </span>
                    </a>
                    {r.originalPinned && <Icon name='push-pin' size='sm' title='Закреплён вручную' />}
                    {!isOrig && onMakeOriginal && (
                        <Button
                            hierarchy='secondary'
                            size='small'
                            onClick={() => onMakeOriginal(r.id)}
                        >
                            Сделать оригиналом
                        </Button>
                    )}
                </div>
            );
        })}
    </div>
);

export const DuplicateSummary = ({ related = [], variant = 'tooltip', sourceLead = null, onNavigate = null, onMakeOriginal = null, isDuplicate = true }) => {
    if (variant === 'section') {
        return <SectionVariant related={related} sourceLead={sourceLead} onNavigate={onNavigate} onMakeOriginal={onMakeOriginal} />;
    }
    return <TooltipVariant related={related} sourceLead={sourceLead} isDuplicate={isDuplicate} />;
};

export default DuplicateSummary;
